<script setup lang="ts">
/**
 * Renders `actionSheet()` and `confirmSheet()` requests (actionSheet.ts), one at a time, on the modal layer so
 * they show above any sheet. Phones: iOS-style grouped buttons rising from the bottom with a separate Cancel.
 * ≥ 640px: a compact centred dialog. Keyboard: focus moves in (to Cancel for destructive confirms), arrows move
 * between buttons, Tab is trapped, Escape / Android back cancel. A destructive confirm opens with a warning
 * haptic; `hold` confirms need a 600ms press (pointer, Enter or Space) with a fill showing progress.
 *
 * Mount once per app (App.vue, dev roots). The first mounted host renders; others stay idle.
 */
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'
import { useMediaQuery } from '@vueuse/core'
import { haptic } from '@/native/haptics'
import { pushBackHandler } from '@/native/backStack'
import { vPress } from '@/directives/press'
import { registerActionSheetHost, retireSheetRequest, sheetRequests, type SheetRequest } from './actionSheet'

/** How long a hold confirm must be held. */
const HOLD_MS = 600

const host = registerActionSheetHost()
onBeforeUnmount(host.dispose)

const wide = useMediaQuery('(min-width: 640px)')
const shown = ref<SheetRequest | null>(null)
const visible = ref(false)
const panel = ref<HTMLElement | null>(null)
const ids = { title: useId(), message: useId(), hint: useId() }
let settled = false
let offBack: (() => void) | null = null
let prevFocus: HTMLElement | null = null

const actionsReq = computed(() => (shown.value?.kind === 'actions' ? shown.value : null))
const confirmReq = computed(() => (shown.value?.kind === 'confirm' ? shown.value : null))
const title = computed(() => shown.value?.options.title)
const message = computed(() => shown.value?.options.message)
const cancelLabel = computed(() => shown.value?.options.cancelLabel || 'Cancel')

watch(
    () => (host.active.value ? sheetRequests[0] : undefined),
    (req) => {
        if (req && !shown.value) present(req)
    },
    { immediate: true },
)

/**
 * A long press opens most sheets: when the finger lifts, the browser's click lands on whatever is under it
 * now (a button or the scrim) and would choose or cancel at once. Only take pointer clicks once a press has
 * started inside the sheet (keyboard clicks have detail 0 and always count).
 */
let pointerSeen = false
function onRootPointerDown() {
    pointerSeen = true
}
function onRootClickCapture(e: MouseEvent) {
    if (pointerSeen || e.detail === 0) return
    e.stopPropagation()
    e.preventDefault()
}

/**
 * A press that starts and ends on the scrim closes the sheet without waiting for the click: iOS skips the
 * synthesized click on a tap that drifts or lingers, and on the first tap after a long press. The release of the
 * press that opened the sheet never counts (it went down on the row, not here).
 */
let scrimPointer = -1
function onScrimPointerDown(e: PointerEvent) {
    scrimPointer = e.isPrimary ? e.pointerId : -1
}
function onScrimPointerUp(e: PointerEvent) {
    if (e.pointerId !== scrimPointer) return
    scrimPointer = -1
    cancel()
}
function onScrimPointerCancel() {
    scrimPointer = -1
}

function present(req: SheetRequest) {
    shown.value = req
    settled = false
    pointerSeen = false
    scrimPointer = -1
    visible.value = true
    prevFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    offBack = pushBackHandler(() => {
        cancel()
        return true
    }, { priority: 100 })
    if (req.kind === 'confirm' && req.options.destructive) haptic('warning')
    void nextTick(() => {
        const p = panel.value
        if (!p) return
        const target =
            p.querySelector<HTMLElement>('[data-autofocus]:not(:disabled)') ?? p.querySelector<HTMLElement>('button:not(:disabled)')
        ;(target ?? p).focus({ preventScroll: true })
    })
}

function finish(value: string | boolean | null) {
    const req = shown.value
    if (!req || settled) return
    settled = true
    stopHold()
    offBack?.()
    offBack = null
    if (req.kind === 'actions') req.resolve(typeof value === 'string' ? value : null)
    else req.resolve(value === true)
    visible.value = false
}

function choose(id: string) {
    finish(id)
}
function cancel() {
    finish(confirmReq.value ? false : null)
}
function confirm() {
    if (confirmReq.value?.options.hold) return // holds confirm through the hold, never a plain click
    finish(true)
}

function onAfterLeave() {
    const req = shown.value
    shown.value = null
    if (req) retireSheetRequest(req)
    const el = prevFocus
    prevFocus = null
    if (el?.isConnected && (!document.activeElement || document.activeElement === document.body)) el.focus({ preventScroll: true })
}

// ── hold to confirm ────────────────────────────────────────────────────────────────────────────────────────
const holding = ref(false)
let holdTimer: ReturnType<typeof setTimeout> | null = null

function startHold() {
    if (settled || holding.value) return
    holding.value = true
    holdTimer = setTimeout(() => {
        holdTimer = null
        holding.value = false
        haptic(confirmReq.value?.options.destructive ? 'heavy' : 'commit')
        finish(true)
    }, HOLD_MS)
}
function stopHold() {
    if (holdTimer) clearTimeout(holdTimer)
    holdTimer = null
    holding.value = false
}
function onHoldPointerDown(e: PointerEvent) {
    if (!e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0)) return
    startHold()
}
function onHoldKeydown(e: KeyboardEvent) {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    if (!e.repeat) startHold()
}
function onHoldKeyup(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') stopHold()
}

// ── keyboard ───────────────────────────────────────────────────────────────────────────────────────────────
function onKeydown(e: KeyboardEvent) {
    const p = panel.value
    if (!p) return
    const buttons = [...p.querySelectorAll<HTMLElement>('button:not(:disabled)')]
    if (!buttons.length) return
    const at = buttons.indexOf(document.activeElement as HTMLElement)
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const step = e.key === 'ArrowDown' ? 1 : -1
        buttons[(at + step + buttons.length) % buttons.length].focus()
    } else if (e.key === 'Tab') {
        if (e.shiftKey && at <= 0) {
            e.preventDefault()
            buttons[buttons.length - 1].focus()
        } else if (!e.shiftKey && at === buttons.length - 1) {
            e.preventDefault()
            buttons[0].focus()
        }
    }
}

onBeforeUnmount(() => {
    stopHold()
    offBack?.()
})
</script>

<template>
    <Teleport to="body">
        <div v-if="shown" class="as-root" :class="{ 'is-wide': wide }" @pointerdown.capture="onRootPointerDown" @click.capture="onRootClickCapture">
            <Transition name="as-fade" appear>
                <div
                    v-if="visible"
                    class="as-scrim"
                    aria-hidden="true"
                    @pointerdown="onScrimPointerDown"
                    @pointerup="onScrimPointerUp"
                    @pointercancel="onScrimPointerCancel"
                    @click="cancel"
                />
            </Transition>
            <Transition :name="wide ? 'as-pop' : 'as-up'" appear @after-leave="onAfterLeave">
                <div
                    v-if="visible"
                    ref="panel"
                    class="as-panel"
                    :class="{ 'is-confirm': !!confirmReq }"
                    :role="confirmReq ? 'alertdialog' : 'dialog'"
                    aria-modal="true"
                    :aria-labelledby="title ? ids.title : undefined"
                    :aria-describedby="message ? ids.message : undefined"
                    :aria-label="title ? undefined : 'Actions'"
                    tabindex="-1"
                    @keydown="onKeydown"
                >
                    <!-- Action list -->
                    <template v-if="actionsReq">
                        <div class="as-group">
                            <div v-if="title || message" class="as-head">
                                <p v-if="title" :id="ids.title" class="as-title">{{ title }}</p>
                                <p v-if="message" :id="ids.message" class="as-message">{{ message }}</p>
                            </div>
                            <button
                                v-for="a in actionsReq.options.actions"
                                :key="a.id"
                                v-press.row
                                type="button"
                                class="as-btn"
                                :class="{ 'is-destructive': a.role === 'destructive' }"
                                :disabled="a.disabled"
                                @click="choose(a.id)"
                            >
                                <i v-if="a.icon" class="as-icon" :class="a.icon" aria-hidden="true" />
                                <span class="as-label">{{ a.label }}</span>
                            </button>
                        </div>
                        <div class="as-group as-cancel-group">
                            <button v-press.row type="button" class="as-btn is-cancel" @click="cancel">{{ cancelLabel }}</button>
                        </div>
                    </template>

                    <!-- Confirm -->
                    <template v-else-if="confirmReq">
                        <div class="as-group">
                            <div class="as-head">
                                <p :id="ids.title" class="as-title">{{ title }}</p>
                                <p v-if="message" :id="ids.message" class="as-message">{{ message }}</p>
                            </div>
                            <div class="as-confirm-row">
                                <button
                                    v-if="wide"
                                    v-press.pill
                                    type="button"
                                    class="as-btn is-cancel"
                                    :data-autofocus="confirmReq.options.destructive ? '' : undefined"
                                    @click="cancel"
                                >
                                    {{ cancelLabel }}
                                </button>
                                <button
                                    v-press="wide ? 'pill' : 'row'"
                                    type="button"
                                    class="as-btn is-confirm"
                                    :class="{ 'is-destructive': confirmReq.options.destructive, 'is-hold': confirmReq.options.hold, 'is-holding': holding }"
                                    :data-autofocus="confirmReq.options.destructive ? undefined : ''"
                                    :aria-describedby="confirmReq.options.hold ? ids.hint : undefined"
                                    @click="confirm"
                                    @pointerdown="confirmReq.options.hold && onHoldPointerDown($event)"
                                    @pointerup="stopHold"
                                    @pointerleave="stopHold"
                                    @pointercancel="stopHold"
                                    @keydown="confirmReq.options.hold && onHoldKeydown($event)"
                                    @keyup="onHoldKeyup"
                                    @contextmenu.prevent
                                >
                                    <span v-if="confirmReq.options.hold" class="as-fill" aria-hidden="true" />
                                    <span class="as-label">{{ confirmReq.options.confirmLabel }}</span>
                                    <span v-if="confirmReq.options.hold" :id="ids.hint" class="as-hint">Press and hold</span>
                                </button>
                            </div>
                        </div>
                        <div v-if="!wide" class="as-group as-cancel-group">
                            <button
                                v-press.row
                                type="button"
                                class="as-btn is-cancel"
                                :data-autofocus="confirmReq.options.destructive ? '' : undefined"
                                @click="cancel"
                            >
                                {{ cancelLabel }}
                            </button>
                        </div>
                    </template>
                </div>
            </Transition>
        </div>
    </Teleport>
</template>

<style scoped>
.as-root {
    --as-surface: rgb(250 250 250 / 0.86);
    --as-surface-solid: #ffffff;
    --as-hairline: rgb(60 60 67 / 0.16);
    --as-text: var(--lh-text-1, #242424);
    --as-muted: var(--lh-text-3, #737373);
    --as-danger: #dc2626;
    --as-danger-fill: rgb(220 38 38 / 0.14);
    position: fixed;
    inset: 0;
    z-index: var(--z-sheet-modal, 10070);
    /* It opens under a finger still held from a long press: nothing in it should start a selection. */
    -webkit-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
}
html.dark .as-root {
    --as-surface: rgb(41 37 36 / 0.84);
    --as-surface-solid: #292524;
    --as-hairline: rgb(255 255 255 / 0.1);
    --as-danger: #f87171;
    --as-danger-fill: rgb(248 113 113 / 0.18);
}

.as-scrim {
    position: absolute;
    inset: 0;
    background: rgb(10 10 10 / 0.32);
    touch-action: none;
}
html.dark .as-scrim {
    background: var(--lh-scrim, rgb(0 0 0 / 0.64));
}

/* ── Phone: grouped buttons at the bottom ── */
.as-panel {
    position: absolute;
    left: 8px;
    right: 8px;
    bottom: calc(8px + env(safe-area-inset-bottom, 0px));
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 480px;
    margin: 0 auto;
    outline: none;
}
.as-group {
    overflow: hidden;
    border-radius: 18px;
    background: var(--as-surface);
    -webkit-backdrop-filter: blur(28px) saturate(1.8);
    backdrop-filter: blur(28px) saturate(1.8);
}
.as-head {
    padding: 14px 20px 12px;
    text-align: center;
}
.as-title {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.35;
    color: var(--as-muted);
}
.as-message {
    margin: 3px 0 0;
    font-size: 13px;
    line-height: 1.4;
    color: var(--as-muted);
}
.as-btn {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    min-height: 56px;
    padding: 0 20px;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 17px;
    font-weight: 400;
    color: var(--as-text);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}
.as-head + .as-btn,
.as-btn + .as-btn,
.as-head + .as-confirm-row {
    border-top: 1px solid var(--as-hairline);
}
.as-btn:disabled {
    opacity: 0.4;
    cursor: default;
}
.as-btn:focus-visible {
    outline: 2px solid var(--agent-accent-ring, rgb(251 76 31 / 0.4));
    outline-offset: -4px;
    border-radius: 14px;
}
.as-btn.is-destructive {
    color: var(--as-danger);
}
.as-icon {
    width: 20px;
    font-size: 17px;
    text-align: center;
    opacity: 0.85;
}
.as-cancel-group {
    background: var(--as-surface-solid);
}
.as-btn.is-cancel {
    font-weight: 600;
}
.as-confirm-row {
    display: flex;
}

/* Hold to confirm */
.as-btn.is-hold {
    flex-direction: column;
    gap: 1px;
    overflow: hidden;
    touch-action: none;
    -webkit-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
}
.as-fill {
    position: absolute;
    inset: 0;
    background: var(--as-danger-fill);
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform 220ms var(--lh-ease-out, ease-out);
    pointer-events: none;
}
.as-btn.is-hold:not(.is-destructive) .as-fill {
    background: rgb(15 15 15 / 0.08);
}
html.dark .as-btn.is-hold:not(.is-destructive) .as-fill {
    background: rgb(255 255 255 / 0.1);
}
.as-btn.is-holding .as-fill {
    transform: scaleX(1);
    transition: transform 600ms linear;
}
.as-label,
.as-hint {
    position: relative;
}
.as-hint {
    font-size: 12px;
    line-height: 1.2;
    color: var(--as-muted);
}

/* ── ≥ 640px: a compact centred dialog ── */
.as-root.is-wide {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
}
.as-root.is-wide .as-panel {
    position: relative;
    left: auto;
    right: auto;
    bottom: auto;
    width: 320px;
    margin: 0;
    gap: 0;
    overflow: hidden;
    border-radius: 18px;
    background: var(--as-surface-solid);
    box-shadow: var(--agent-float-shadow, 0 24px 64px -20px rgb(15 15 15 / 0.35));
}
.as-root.is-wide .as-panel.is-confirm {
    width: 400px;
}
.as-root.is-wide .as-group {
    border-radius: 0;
    background: transparent;
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
}
.as-root.is-wide .as-cancel-group {
    border-top: 1px solid var(--as-hairline);
}
.as-root.is-wide .as-btn {
    min-height: 44px;
    font-size: 15px;
}
.as-root.is-wide .as-head {
    padding: 18px 20px 14px;
}
.as-root.is-wide .as-panel.is-confirm .as-head {
    text-align: left;
    padding: 20px 20px 4px;
}
.as-root.is-wide .as-panel.is-confirm .as-title {
    font-size: 16px;
    color: var(--as-text);
}
.as-root.is-wide .as-panel.is-confirm .as-message {
    margin-top: 6px;
    font-size: 14px;
    color: var(--lh-text-2, #525252);
}
.as-root.is-wide .as-confirm-row {
    justify-content: flex-end;
    gap: 8px;
    padding: 16px 20px 20px;
    border-top: 0;
}
.as-root.is-wide .as-confirm-row .as-btn {
    width: auto;
    min-height: 40px;
    padding: 0 18px;
    border: 0;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 500;
}
.as-root.is-wide .as-confirm-row .as-btn.is-cancel {
    background: var(--agent-hover, rgb(15 15 15 / 0.05));
}
.as-root.is-wide .as-confirm-row .as-btn.is-confirm {
    background: var(--lh-text-1, #242424);
    color: var(--lh-page, #fafafa);
}
.as-root.is-wide .as-confirm-row .as-btn.is-confirm.is-destructive {
    background: #dc2626;
    color: #fff;
}
.as-root.is-wide .as-confirm-row .as-btn.is-hold .as-hint {
    color: inherit;
    opacity: 0.75;
}
.as-root.is-wide .as-confirm-row .as-btn .as-fill {
    background: rgb(255 255 255 / 0.22);
}
@media (hover: hover) and (pointer: fine) {
    .as-btn:not(:disabled):hover {
        background-color: var(--agent-hover, rgb(15 15 15 / 0.04));
    }
    .as-root.is-wide .as-confirm-row .as-btn.is-confirm:hover {
        background: var(--lh-text-2, #525252);
    }
    .as-root.is-wide .as-confirm-row .as-btn.is-confirm.is-destructive:hover {
        background: #b91c1c;
    }
}

/* ── Motion ── */
.as-fade-enter-active,
.as-fade-leave-active {
    transition: opacity var(--lh-dur-base, 240ms) var(--lh-ease-out, ease-out);
}
.as-fade-enter-from,
.as-fade-leave-to {
    opacity: 0;
}
.as-up-enter-active {
    transition: transform var(--lh-dur-push, 340ms) var(--lh-ease-ios, ease-out);
}
.as-up-leave-active {
    transition: transform var(--lh-dur-pop, 280ms) var(--lh-ease-ios, ease-out);
}
.as-up-enter-from,
.as-up-leave-to {
    transform: translate3d(0, calc(100% + 24px + env(safe-area-inset-bottom, 0px)), 0);
}
.as-pop-enter-active {
    transition:
        opacity var(--lh-dur-fast, 150ms) var(--lh-ease-out, ease-out),
        transform var(--lh-dur-base, 240ms) var(--lh-spring-soft, ease-out);
}
.as-pop-leave-active {
    transition:
        opacity var(--lh-dur-fast, 150ms) var(--lh-ease-out, ease-out),
        transform var(--lh-dur-fast, 150ms) var(--lh-ease-out, ease-out);
}
.as-pop-enter-from,
.as-pop-leave-to {
    opacity: 0;
    transform: scale(0.95);
}
@media (prefers-reduced-motion: reduce) {
    .as-up-enter-active,
    .as-up-leave-active {
        transition: opacity var(--lh-dur-fast, 120ms) ease-out;
    }
    .as-up-enter-from,
    .as-up-leave-to {
        transform: none;
        opacity: 0;
    }
    .as-pop-enter-from,
    .as-pop-leave-to {
        transform: none;
    }
}
</style>
